import { useEffect, useState, type DragEvent, type MouseEvent } from 'react'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import { MiddleEllipsis } from '../Common/MiddleEllipsis'
import { ContextMenu, type ActionMenuItem } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { useNls } from '../../nls/nlsProvider'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import type { RowLockResult } from '../manageRowLock'
import type {
  BoardGroup,
  ChecklistDragPayload,
  ChecklistDropTarget,
  DetailedNodeKind,
  DetailedSelection,
  NodeDragPayload,
  PasteTarget,
  TreeNode,
} from './types'

interface FolderTreeProps {
  /** Single goal mode passes `root` (back-compat). */
  root?: TreeNode | null
  /** Workspace mode passes an array of roots — every goal becomes a top-level
   * row. Mutually exclusive with `root` — when both are passed, `roots` wins. */
  roots?: TreeNode[] | null
  selectionKey: string | null
  onSelect: (selection: DetailedSelection) => void
  onChecklistDragStart: (selection: DetailedSelection & { kind: 'checklist' }) => ChecklistDragPayload | null
  onChecklistDrop: (target: ChecklistDropTarget) => void
  /** Returns true if the currently-being-dragged payload can land on `target`. */
  validateDrop: (target: ChecklistDropTarget) => boolean
  /** Disabled because dragging is unsupported (e.g. detail mode read-only). */
  disableDrag?: boolean
  /** Click the green-tick affordance on a draft-attention row. */
  onAcceptNode?: (nodeId: string) => void
  /** Optional set of ids to force-expand on first render (workspace mode uses
   * this to reveal the Manage-selected node's ancestor path). Empty set means
   * the tree's own auto-expand logic decides. */
  initialExpansion?: Set<string>
  /** Manage chrome only: render the goal roots inside top-level board folders.
   * When present, takes precedence over `roots`/`root`. */
  boardGroups?: BoardGroup[] | null
  /** Bin-only affordance: open the per-item Move dialog. When provided, Bin
   * rows render a "Move" button. Only relevant in manage chrome + team mode. */
  onRequestBinMove?: (elementId: string) => void
  /** Cross-board / re-parent DnD (workspace + manage only). Plain drag = cut,
   * Alt+drag = copy. When null, only the existing checklist DnD is active. */
  onNodeDragStart?: (selection: DetailedSelection, altKey: boolean) => NodeDragPayload | null
  onNodeDrop?: (target: PasteTarget) => void
  validateNodeDrop?: (target: PasteTarget) => boolean
  /** Surface a Copy / Cut / Paste menu on the given row. */
  onContextMenu?: (selection: DetailedSelection, position: { clientX: number; clientY: number }) => void
  /** Manage chrome only: fired when the user tries to open an empty board
   * folder. The screen surfaces a toast like "No items in this board". */
  onEmptyBoardOpen?: (board: BoardGroup) => void
  /** Read peer-lock visual state for a node id. */
  getLockStatus?: (id: string) => RowLockResult
  /** Notify parent of expanded node ids. */
  onExpansionChange?: (expanded: Set<string>) => void
}

const DRAG_MIME = 'application/x-detailed-checklist'
const NODE_DRAG_MIME = 'application/x-detailed-node'

const selectionKey = (selection: DetailedSelection): string => {
  switch (selection.kind) {
    case 'goal':
      return `g:${selection.goalId}`
    case 'task':
      return `t:${selection.goalId}:${selection.taskId}`
    case 'subtask':
      return `s:${selection.goalId}:${selection.taskId}:${selection.subTaskId}`
    case 'checklist':
      return `c:${selection.goalId}:${selection.taskId ?? '_'}:${selection.subTaskId ?? '_'}:${selection.checklistId}`
  }
}

export const treeSelectionKey = selectionKey

const NodeIcon = ({ kind, isOpen }: { kind: DetailedNodeKind; isOpen: boolean }) => {
  const props = { fontSize: 'inherit' as const }
  if (kind === 'goal') return <FlagRoundedIcon {...props} />
  if (kind === 'task') return <AssignmentRoundedIcon {...props} />
  if (kind === 'subtask') return isOpen ? <FolderOpenRoundedIcon {...props} /> : <FolderRoundedIcon {...props} />
  return <ChecklistRoundedIcon {...props} />
}

const diffBadgeClass = (status: TreeNode['diff']): string => {
  switch (status) {
    case 'added':
      return 'detailedTreeRow--added'
    case 'removed':
      return 'detailedTreeRow--removed'
    case 'changed':
      return 'detailedTreeRow--changed'
    default:
      return ''
  }
}

/** Build the drop-target descriptor for a folder row. */
const dropTargetForNode = (selection: DetailedSelection): ChecklistDropTarget | null => {
  if (selection.kind === 'goal') return { kind: 'into-goal', goalId: selection.goalId }
  if (selection.kind === 'task') return { kind: 'into-task', goalId: selection.goalId, taskId: selection.taskId }
  if (selection.kind === 'subtask') {
    return {
      kind: 'into-subtask',
      goalId: selection.goalId,
      taskId: selection.taskId,
      subTaskId: selection.subTaskId,
    }
  }
  if (selection.kind === 'checklist') {
    return {
      kind: 'before-checklist',
      goalId: selection.goalId,
      taskId: selection.taskId ?? null,
      subTaskId: selection.subTaskId ?? null,
      checklistId: selection.checklistId,
    }
  }
  return null
}

interface TreeRowProps {
  node: TreeNode
  depth: number
  expanded: Set<string>
  setExpanded: (next: Set<string>) => void
  selectionKey: string | null
  onSelect: (selection: DetailedSelection) => void
  onChecklistDragStart: (selection: DetailedSelection & { kind: 'checklist' }) => ChecklistDragPayload | null
  onChecklistDrop: (target: ChecklistDropTarget) => void
  validateDrop: (target: ChecklistDropTarget) => boolean
  disableDrag: boolean
  onAcceptNode?: (nodeId: string) => void
  /** When set, this row + its descendants render a "Move" button that fires
   * the callback. Wired by BoardGroupedTree only on the Bin board. */
  onRequestBinMove?: (elementId: string) => void
  onNodeDragStart?: (selection: DetailedSelection, altKey: boolean) => NodeDragPayload | null
  onNodeDrop?: (target: PasteTarget) => void
  validateNodeDrop?: (target: PasteTarget) => boolean
  onContextMenu?: (selection: DetailedSelection, position: { clientX: number; clientY: number }) => void
  getLockStatus?: (id: string) => RowLockResult
}

const TreeRow = ({
  node,
  depth,
  expanded,
  setExpanded,
  selectionKey: activeSelectionKey,
  onSelect,
  onChecklistDragStart,
  onChecklistDrop,
  validateDrop,
  disableDrag,
  onAcceptNode,
  onRequestBinMove,
  onNodeDragStart,
  onNodeDrop,
  validateNodeDrop,
  onContextMenu,
  getLockStatus,
}: TreeRowProps) => {
  const { t } = useNls()
  const hasChildren = node.children.length > 0
  const isOpen = expanded.has(node.id)
  const ownSelectionKey = selectionKey(node.selection)
  const isActive = activeSelectionKey === ownSelectionKey
  const [dragState, setDragState] = useState<'idle' | 'valid' | 'invalid'>('idle')

  const toggle = () => {
    const next = new Set(expanded)
    if (isOpen) next.delete(node.id)
    else next.add(node.id)
    setExpanded(next)
  }

  const handleRowClick = () => {
    if (node.selectable) onSelect(node.selection)
  }

  // ---- Drag source (checklist only, only the grip) ------------------------
  const handleGripDragStart = (event: DragEvent<HTMLSpanElement>) => {
    if (node.kind !== 'checklist' || node.selection.kind !== 'checklist' || disableDrag) {
      event.preventDefault()
      return
    }
    const payload = onChecklistDragStart(node.selection)
    if (!payload) {
      event.preventDefault()
      return
    }
    event.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload))
    event.dataTransfer.effectAllowed = 'move'
  }

  // ---- Drop target --------------------------------------------------------
  const dropTarget = dropTargetForNode(node.selection)
  const nodeDropTarget: PasteTarget = { kind: 'selection', selection: node.selection }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disableDrag) return
    if (event.dataTransfer.types.includes(NODE_DRAG_MIME) && validateNodeDrop) {
      const ok = validateNodeDrop(nodeDropTarget)
      setDragState(ok ? 'valid' : 'invalid')
      event.dataTransfer.dropEffect = ok ? (event.altKey ? 'copy' : 'move') : 'none'
      if (ok) event.preventDefault()
      return
    }
    if (!dropTarget) return
    if (!event.dataTransfer.types.includes(DRAG_MIME)) return
    const ok = validateDrop(dropTarget)
    setDragState(ok ? 'valid' : 'invalid')
    event.dataTransfer.dropEffect = ok ? 'move' : 'none'
    if (ok) event.preventDefault()
  }

  const handleDragLeave = () => {
    if (dragState !== 'idle') setDragState('idle')
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disableDrag) return
    if (event.dataTransfer.types.includes(NODE_DRAG_MIME) && onNodeDrop && validateNodeDrop) {
      event.preventDefault()
      setDragState('idle')
      if (validateNodeDrop(nodeDropTarget)) onNodeDrop(nodeDropTarget)
      return
    }
    if (!dropTarget) return
    if (!event.dataTransfer.types.includes(DRAG_MIME)) return
    event.preventDefault()
    setDragState('idle')
    if (validateDrop(dropTarget)) onChecklistDrop(dropTarget)
  }

  // ---- Goal/Task/Subtask drag source (whole-row drag) ---------------------
  const isNodeDraggable =
    Boolean(onNodeDragStart) && !disableDrag && node.diff !== 'removed' && node.kind !== 'checklist'

  const handleRowDragStart = (event: DragEvent<HTMLDivElement>) => {
    if (!onNodeDragStart || disableDrag) {
      event.preventDefault()
      return
    }
    if (node.kind === 'checklist') {
      // Checklist drag uses its own grip; suppress whole-row drag.
      event.preventDefault()
      return
    }
    const payload = onNodeDragStart(node.selection, event.altKey)
    if (!payload) {
      event.preventDefault()
      return
    }
    event.dataTransfer.setData(NODE_DRAG_MIME, JSON.stringify(payload))
    event.dataTransfer.effectAllowed = event.altKey ? 'copy' : 'move'
  }

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!onContextMenu) return
    event.preventDefault()
    onContextMenu(node.selection, { clientX: event.clientX, clientY: event.clientY })
  }

  const { rowLockedByPeer, lockTitle } = getLockStatus
    ? getLockStatus(node.id)
    : { rowLockedByPeer: false, lockTitle: null }

  const rowMarkerKey: 'warn' | 'new' | 'edit' | 'del' | undefined =
    node.validation === 'invalid'
      ? 'warn'
      : node.diff === 'added'
        ? 'new'
        : node.diff === 'changed'
          ? 'edit'
          : node.diff === 'removed'
            ? 'del'
            : undefined

  return (
    <div className="detailedTreeBranch">
      <div
        className={`detailedTreeRow ${isActive ? 'detailedTreeRow--active' : ''} ${diffBadgeClass(node.diff)} ${
          dragState === 'valid'
            ? 'detailedTreeRow--dropValid'
            : dragState === 'invalid'
              ? 'detailedTreeRow--dropInvalid'
              : ''
        }${rowLockedByPeer ? ' rowLockedByPeer' : ''}`}
        data-mk={rowMarkerKey}
        aria-disabled={rowLockedByPeer || undefined}
        title={lockTitle ?? undefined}
        style={{ paddingInlineStart: 8 + depth * 14 }}
        draggable={isNodeDraggable}
        onDragStart={isNodeDraggable ? handleRowDragStart : undefined}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onContextMenu={onContextMenu ? handleContextMenu : undefined}
      >
        {hasChildren ? (
          <Pressable
            className={`detailedTreeChevron ${isOpen ? 'detailedTreeChevron--open' : ''}`}
            onClick={toggle}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            aria-expanded={isOpen}
          >
            <ChevronRightRoundedIcon fontSize="inherit" />
          </Pressable>
        ) : (
          <span className="detailedTreeChevronSpacer" aria-hidden="true" />
        )}
        <Pressable
          className="detailedTreeLabel"
          onClick={handleRowClick}
          title={lockTitle ?? node.title}
        >
          <span className="detailedTreeIcon" aria-hidden="true">
            <NodeIcon kind={node.kind} isOpen={isOpen} />
          </span>
          <span className="detailedTreeTitle"><MiddleEllipsis text={node.title} /></span>
          {rowLockedByPeer && (
            <span className="rowLockedByPeerBadge" title={lockTitle ?? undefined}>
              <LockRoundedIcon fontSize="inherit" />
            </span>
          )}
          {node.unsaved && (
            <span
              className="detailedTreeUnsaved"
              aria-label={t('NLSMarkerUnsavedChanges')}
              title={t('NLSMarkerUnsavedChanges')}
            >
              <span className="detailedTreeUnsavedText" aria-hidden="true">*</span>
            </span>
          )}
          {node.diff === 'added' && (
            <span
              className="detailedTreeDiffChip detailedTreeDiffChip--added is-unread"
              aria-label={t('NLSMarkerNew')}
              title={t('NLSMarkerNew')}
            >
              <span className="detailedTreeMarkerGlyph" aria-hidden="true">+</span>
              <span className="detailedTreeMarkerText">{t('NLSMarkerNew')}</span>
            </span>
          )}
          {node.diff === 'removed' && (
            <span
              className="detailedTreeDiffChip detailedTreeDiffChip--removed"
              aria-label={t('NLSMarkerRemoved')}
              title={t('NLSMarkerRemoved')}
            >
              <span className="detailedTreeMarkerGlyph" aria-hidden="true">−</span>
              <span className="detailedTreeMarkerText">{t('NLSMarkerRemoved')}</span>
            </span>
          )}
          {node.diff === 'changed' && (
            <span
              className="detailedTreeDiffChip detailedTreeDiffChip--changed"
              aria-label={t('NLSMarkerEdited')}
              title={t('NLSMarkerEdited')}
            >
              <span className="detailedTreeMarkerGlyph" aria-hidden="true">✎</span>
              <span className="detailedTreeMarkerText">{t('NLSMarkerEdited')}</span>
            </span>
          )}
          {node.validation === 'invalid' && (
            <span
              className="detailedTreeDiffChip detailedTreeDiffChip--invalid"
              aria-label={t('NLSMarkerNeedsAttention')}
              title={t('NLSMarkerNeedsAttention')}
            >
              <span className="detailedTreeMarkerGlyph" aria-hidden="true">!</span>
              <span className="detailedTreeMarkerText">{t('NLSMarkerNeedsAttention')}</span>
            </span>
          )}
        </Pressable>
        {onRequestBinMove && node.kind !== 'checklist' && (
          <IconButton
            className="detailedTreeBinMove"
            variant="secondary"
            size="sm"
            onClick={(event) => {
              event.stopPropagation()
              onRequestBinMove(node.id)
            }}
            label="Move to another board"
            icon={<DriveFileMoveRoundedIcon fontSize="inherit" />}
          />
        )}
        {node.needsDraftAttention && onAcceptNode && node.diff !== 'removed' && node.validation === 'ok' && (
          <IconButton
            className="detailedTreeAccept"
            variant="secondary"
            size="sm"
            onClick={() => onAcceptNode(node.id)}
            label="Acknowledge draft change"
            icon={<CheckCircleOutlineRoundedIcon fontSize="inherit" />}
          />
        )}
        {node.kind === 'checklist' && !disableDrag && node.diff !== 'removed' && (
          <span
            className="detailedTreeGrip"
            draggable
            onDragStart={handleGripDragStart}
            aria-label="Drag to move"
            title="Drag to move"
          >
            <DragIndicatorRoundedIcon fontSize="inherit" />
          </span>
        )}
      </div>
      {hasChildren && isOpen && (
        <div className="detailedTreeChildren">
          {node.children.map((child) => (
            <TreeRow
              key={`${child.kind}-${child.id}`}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              setExpanded={setExpanded}
              selectionKey={activeSelectionKey}
              onSelect={onSelect}
              onChecklistDragStart={onChecklistDragStart}
              onChecklistDrop={onChecklistDrop}
              validateDrop={validateDrop}
              disableDrag={disableDrag}
              onAcceptNode={onAcceptNode}
              onRequestBinMove={onRequestBinMove}
              onNodeDragStart={onNodeDragStart}
              onNodeDrop={onNodeDrop}
              validateNodeDrop={validateNodeDrop}
              onContextMenu={onContextMenu}
              getLockStatus={getLockStatus}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Walk a single root once and return the ids that should be expanded on first
 * render: the root itself, plus any branch ancestor of a diff node. */
const computeInitialExpansionForRoot = (root: TreeNode): Set<string> => {
  const expanded = new Set<string>()
  expanded.add(root.id)
  const walk = (node: TreeNode): boolean => {
    let containsDiff = node.diff !== 'unchanged'
    for (const child of node.children) {
      const childContains = walk(child)
      containsDiff = containsDiff || childContains
    }
    if (containsDiff) expanded.add(node.id)
    return containsDiff
  }
  walk(root)
  return expanded
}

/**
 * Workspace mode initial expansion: start everything collapsed, expand only
 * the path that leads to ids in `force`. Used when Manage's "Advanced" entry
 * focuses a specific node — we reveal the ancestor chain to it and leave the
 * rest tidy.
 */
const computeWorkspaceExpansion = (roots: TreeNode[], force: Set<string>): Set<string> => {
  const expanded = new Set<string>()
  if (force.size === 0) return expanded
  const walk = (node: TreeNode): boolean => {
    let containsForced = force.has(node.id)
    for (const child of node.children) {
      const childContains = walk(child)
      containsForced = containsForced || childContains
    }
    if (containsForced) expanded.add(node.id)
    return containsForced
  }
  for (const root of roots) walk(root)
  return expanded
}

export const FolderTree = ({
  root,
  roots,
  selectionKey: activeKey,
  onSelect,
  onChecklistDragStart,
  onChecklistDrop,
  validateDrop,
  disableDrag = false,
  onAcceptNode,
  initialExpansion,
  boardGroups,
  onRequestBinMove,
  onNodeDragStart,
  onNodeDrop,
  validateNodeDrop,
  onContextMenu,
  onEmptyBoardOpen,
  getLockStatus,
  onExpansionChange,
}: FolderTreeProps) => {
  // Normalize to an array internally — single-root callers and workspace
  // callers share the same render path below.
  const effectiveRoots = roots ?? (root ? [root] : [])

  // Compute initial expansion. Workspace mode (initialExpansion non-empty)
  // expands the ancestor chain to the focused node; single-root mode keeps
  // the old "top level + diff branches" behaviour.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (initialExpansion && initialExpansion.size > 0) {
      return computeWorkspaceExpansion(effectiveRoots, initialExpansion)
    }
    if (root) return computeInitialExpansionForRoot(root)
    return new Set<string>()
  })

  useEffect(() => {
    onExpansionChange?.(expanded)
  }, [expanded, onExpansionChange])

  // Recompute when the root identity changes (fresh AI generate, new
  // workspace open, etc.). We key on the joined ids so multi-goal mode also
  // re-runs when goals are added/removed.
  const rootsKey = effectiveRoots.map((node) => node.id).join('|')
  const expansionKey = initialExpansion ? [...initialExpansion].sort().join('|') : ''
  useEffect(() => {
    if (initialExpansion && initialExpansion.size > 0) {
      setExpanded(computeWorkspaceExpansion(effectiveRoots, initialExpansion))
      return
    }
    if (effectiveRoots.length === 1) {
      setExpanded(computeInitialExpansionForRoot(effectiveRoots[0]))
      return
    }
    setExpanded(new Set<string>())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootsKey, expansionKey])

  if (boardGroups && boardGroups.length > 0) {
    return (
      <BoardGroupedTree
        groups={boardGroups}
        expanded={expanded}
        setExpanded={setExpanded}
        selectionKey={activeKey}
        onSelect={onSelect}
        onChecklistDragStart={onChecklistDragStart}
        onChecklistDrop={onChecklistDrop}
        validateDrop={validateDrop}
        disableDrag={disableDrag}
        onAcceptNode={onAcceptNode}
        onRequestBinMove={onRequestBinMove}
        onNodeDragStart={onNodeDragStart}
        onNodeDrop={onNodeDrop}
        validateNodeDrop={validateNodeDrop}
        onContextMenu={onContextMenu}
        onEmptyBoardOpen={onEmptyBoardOpen}
        getLockStatus={getLockStatus}
      />
    )
  }

  if (effectiveRoots.length === 0) {
    return (
      <div className="detailedTreePanel">
        <p className="detailedTreeEmpty">No items to manage.</p>
      </div>
    )
  }

  return (
    <div className="detailedTreePanel">
      <div className="detailedTreeHeader">
        <TaskAltRoundedIcon fontSize="inherit" />
        <span>{effectiveRoots.length > 1 ? 'Workspace' : 'Structure'}</span>
      </div>
      <div className="detailedTreeScroll">
        {effectiveRoots.map((node) => (
          <TreeRow
            key={`${node.kind}-${node.id}`}
            node={node}
            depth={0}
            expanded={expanded}
            setExpanded={setExpanded}
            selectionKey={activeKey}
            onSelect={onSelect}
            onChecklistDragStart={onChecklistDragStart}
            onChecklistDrop={onChecklistDrop}
            validateDrop={validateDrop}
            disableDrag={disableDrag}
            onAcceptNode={onAcceptNode}
            onNodeDragStart={onNodeDragStart}
            onNodeDrop={onNodeDrop}
            validateNodeDrop={validateNodeDrop}
            onContextMenu={onContextMenu}
            getLockStatus={getLockStatus}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Manage's board-grouped layout. The 4 boards (or 1 in personal mode) render
// as collapsible folders above their goal roots. Each board owns its own
// open/closed state, defaulting to the active board expanded.
// ---------------------------------------------------------------------------

interface BoardGroupedTreeProps {
  groups: BoardGroup[]
  expanded: Set<string>
  setExpanded: (next: Set<string>) => void
  selectionKey: string | null
  onSelect: (selection: DetailedSelection) => void
  onChecklistDragStart: (selection: DetailedSelection & { kind: 'checklist' }) => ChecklistDragPayload | null
  onChecklistDrop: (target: ChecklistDropTarget) => void
  validateDrop: (target: ChecklistDropTarget) => boolean
  disableDrag: boolean
  onAcceptNode?: (nodeId: string) => void
  onRequestBinMove?: (elementId: string) => void
  onNodeDragStart?: (selection: DetailedSelection, altKey: boolean) => NodeDragPayload | null
  onNodeDrop?: (target: PasteTarget) => void
  validateNodeDrop?: (target: PasteTarget) => boolean
  onContextMenu?: (selection: DetailedSelection, position: { clientX: number; clientY: number }) => void
  /** Fires when the user tries to open a board folder that has no items. */
  onEmptyBoardOpen?: (board: BoardGroup) => void
  getLockStatus?: (id: string) => RowLockResult
}

const BoardGroupedTree = ({
  groups,
  expanded,
  setExpanded,
  selectionKey,
  onSelect,
  onChecklistDragStart,
  onChecklistDrop,
  validateDrop,
  disableDrag,
  onAcceptNode,
  onRequestBinMove,
  onNodeDragStart,
  onNodeDrop,
  validateNodeDrop,
  onContextMenu,
  onEmptyBoardOpen,
  getLockStatus,
}: BoardGroupedTreeProps) => {
  // Per-board open/closed map. Initialised from defaultExpanded; user toggles
  // override. Keyed by board so the state survives re-renders even if groups
  // re-compute.
  const [openBoards, setOpenBoards] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const group of groups) init[group.board] = group.defaultExpanded
    return init
  })

  // If a new board appears (shouldn't happen at runtime but safe), inherit
  // its defaultExpanded flag. Existing keys are preserved so user toggles
  // stick across re-renders.
  useEffect(() => {
    setOpenBoards((current) => {
      let changed = false
      const next = { ...current }
      for (const group of groups) {
        if (!(group.board in next)) {
          next[group.board] = group.defaultExpanded
          changed = true
        }
      }
      return changed ? next : current
    })
  }, [groups])

  const renderBoardBranch = (group: BoardGroup) => {
    const isOpen = openBoards[group.board] ?? group.defaultExpanded
    // Functional update reads current state inside the updater so a stale
    // closure on `isOpen` can't drop a click on the floor.
    const toggle = () => {
      if (group.roots.length === 0 && !isOpen) {
        onEmptyBoardOpen?.(group)
        return
      }
      setOpenBoards((current) => {
        const wasOpen = current[group.board] ?? group.defaultExpanded
        return { ...current, [group.board]: !wasOpen }
      })
    }
    return (
      <div key={group.board} className="detailedTreeBranch detailedTreeBoardGroup">
        <BoardHeaderRow
          group={group}
          isOpen={isOpen}
          onToggle={toggle}
          disableDrag={disableDrag}
          onNodeDrop={onNodeDrop}
          validateNodeDrop={validateNodeDrop}
        />
        {isOpen && (
          <div className="detailedTreeChildren">
            {group.roots.length === 0 ? (
              <p className="detailedTreeEmpty detailedTreeEmpty--inline">
                No items on {group.label}.
              </p>
            ) : (
              group.roots.map((node) => (
                <TreeRow
                  key={`${node.kind}-${node.id}`}
                  node={node}
                  depth={1}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  selectionKey={selectionKey}
                  onSelect={onSelect}
                  onChecklistDragStart={onChecklistDragStart}
                  onChecklistDrop={onChecklistDrop}
                  validateDrop={validateDrop}
                  disableDrag={disableDrag}
                  onAcceptNode={onAcceptNode}
                  onRequestBinMove={group.board === 'BIN' ? onRequestBinMove : undefined}
                  onNodeDragStart={onNodeDragStart}
                  onNodeDrop={onNodeDrop}
                  validateNodeDrop={validateNodeDrop}
                  onContextMenu={onContextMenu}
                  getLockStatus={getLockStatus}
                />
              ))
            )}
          </div>
        )}
      </div>
    )
  }

  const topGroups = groups.filter((g) => g.board !== 'BIN')
  const binGroup = groups.find((g) => g.board === 'BIN') ?? null

  return (
    <div className="detailedTreePanel">
      <div className="detailedTreeHeader">
        <TaskAltRoundedIcon fontSize="inherit" />
        <span>Workspace</span>
      </div>
      <div className="detailedTreeScroll">
        {topGroups.map(renderBoardBranch)}
        {binGroup && (
          <>
            <div className="detailedTreeBinFooter detailedTreeBinFooter--inline" aria-hidden="true">
              <hr className="detailedTreeBinDivider" />
            </div>
            {renderBoardBranch(binGroup)}
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Board-folder header row — acts as a drop target for cross-board goal pastes.
// Hover state mirrors TreeRow's valid/invalid drop styles.
// ---------------------------------------------------------------------------

interface BoardHeaderRowProps {
  group: BoardGroup
  isOpen: boolean
  onToggle: () => void
  disableDrag: boolean
  onNodeDrop?: (target: PasteTarget) => void
  validateNodeDrop?: (target: PasteTarget) => boolean
}

const BoardHeaderRow = ({
  group,
  isOpen,
  onToggle,
  disableDrag,
  onNodeDrop,
  validateNodeDrop,
}: BoardHeaderRowProps) => {
  const [dragState, setDragState] = useState<'idle' | 'valid' | 'invalid'>('idle')
  const target: PasteTarget = { kind: 'board', board: group.board }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (disableDrag || !validateNodeDrop) return
    if (!event.dataTransfer.types.includes(NODE_DRAG_MIME)) return
    const ok = validateNodeDrop(target)
    setDragState(ok ? 'valid' : 'invalid')
    event.dataTransfer.dropEffect = ok ? (event.altKey ? 'copy' : 'move') : 'none'
    if (ok) event.preventDefault()
  }

  const handleDragLeave = () => {
    if (dragState !== 'idle') setDragState('idle')
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (disableDrag || !onNodeDrop || !validateNodeDrop) return
    if (!event.dataTransfer.types.includes(NODE_DRAG_MIME)) return
    event.preventDefault()
    setDragState('idle')
    if (validateNodeDrop(target)) onNodeDrop(target)
  }

  const dragClass =
    dragState === 'valid'
      ? 'detailedTreeRow--dropValid'
      : dragState === 'invalid'
        ? 'detailedTreeRow--dropInvalid'
        : ''

  return (
    <div
      className={`detailedTreeRow detailedTreeRow--boardHeader detailedTreeRow--board-${group.board.toLowerCase()} ${dragClass}`}
      style={{ paddingInlineStart: 8 }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Pressable
        className={`detailedTreeChevron ${isOpen ? 'detailedTreeChevron--open' : ''}`}
        onClick={onToggle}
        aria-label={isOpen ? 'Collapse' : 'Expand'}
        aria-expanded={isOpen}
      >
        <ChevronRightRoundedIcon fontSize="inherit" />
      </Pressable>
      <span className="detailedTreeLabel detailedTreeLabel--board">
        <span className="detailedTreeIcon" aria-hidden="true">
          {isOpen ? <FolderOpenRoundedIcon fontSize="inherit" /> : <FolderRoundedIcon fontSize="inherit" />}
        </span>
        <span className="detailedTreeTitle">{group.label}</span>
        <span className="detailedTreeBoardCount" aria-hidden="true">
          {group.roots.length}
        </span>
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Floating context menu — Copy / Cut / Paste. Positioned at the click point;
// caller renders this at the screen level so it sits above the tree.
// ---------------------------------------------------------------------------

interface TreeContextMenuProps {
  position: { clientX: number; clientY: number }
  canCopy: boolean
  canPaste: boolean
  onCopy: () => void
  onCut: () => void
  onPaste: () => void
  onClose: () => void
}

export const TreeContextMenu = ({
  position,
  canCopy,
  canPaste,
  onCopy,
  onCut,
  onPaste,
  onClose,
}: TreeContextMenuProps) => {
  const { t } = useNls()
  const items: ActionMenuItem[] = [
    {
      key: 'copy',
      label: 'Copy',
      disabled: !canCopy,
      onSelect: onCopy,
    },
    {
      key: 'cut',
      label: 'Cut',
      disabled: !canCopy,
      onSelect: onCut,
    },
    {
      key: 'paste',
      label: 'Paste',
      disabled: !canPaste,
      onSelect: onPaste,
    },
  ]

  return (
    <ContextMenu
      open={true}
      position={{ x: position.clientX, y: position.clientY }}
      items={items}
      ariaLabel={t('NLSDetailedViewContextMenuLabel')}
      onClose={onClose}
    />
  )
}
